import { useEffect, useState } from "react";
import api, { formatDate, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "",
  nif: "",
  email: "",
  phone: "",
  address: "",
  postal_code: "",
  city: "",
  province: "",
  country: "España",
  notes: "",
};

export default function ClientesPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: query ? { q: query } : {} });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (c) => {
    setForm({ ...empty, ...c });
    setEditingId(c.id);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/clients/${editingId}`, form);
        toast.success("Cliente actualizado");
      } else {
        await api.post("/clients", form);
        toast.success("Cliente creado");
      }
      setOpen(false);
      load(q);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este cliente?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Cliente eliminado");
    load(q);
  };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
            Clientes
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gestiona la base de clientes a los que facturas.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="new-client-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Nuevo cliente
        </Button>
      </div>

      <Card className="p-5">
        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre, NIF o email..."
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              load(e.target.value);
            }}
            className="pl-9"
            data-testid="client-search-input"
          />
        </div>

        {loading ? (
          <div className="py-10 text-center text-slate-500">Cargando...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-14">
            <Users className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">
              No hay clientes registrados.
            </p>
            <Button
              onClick={openNew}
              className="mt-4 bg-blue-600 hover:bg-blue-700"
              data-testid="empty-new-client"
            >
              Añadir primer cliente
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4">Nombre</th>
                  <th className="py-2 pr-4">NIF/CIF</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Ciudad</th>
                  <th className="py-2 pr-4">Añadido</th>
                  <th className="py-2 pr-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`client-row-${c.id}`}
                  >
                    <td className="py-3 pr-4 font-medium text-slate-900">{c.name}</td>
                    <td className="py-3 pr-4 text-slate-700">{c.nif || "-"}</td>
                    <td className="py-3 pr-4 text-slate-700">{c.email || "-"}</td>
                    <td className="py-3 pr-4 text-slate-700">{c.city || "-"}</td>
                    <td className="py-3 pr-4 text-slate-500 text-xs">
                      {formatDate(c.created_at)}
                    </td>
                    <td className="py-3 pr-4 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(c)}
                        data-testid={`edit-client-${c.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(c.id)}
                        className="text-rose-600 hover:text-rose-700"
                        data-testid={`delete-client-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editar cliente" : "Nuevo cliente"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nombre / Razón social *</Label>
              <Input
                value={form.name}
                onChange={setField("name")}
                data-testid="client-form-name"
              />
            </div>
            <div>
              <Label>NIF / CIF</Label>
              <Input value={form.nif} onChange={setField("nif")} data-testid="client-form-nif" />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={setField("email")}
                data-testid="client-form-email"
              />
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input value={form.phone} onChange={setField("phone")} />
            </div>
            <div className="col-span-2">
              <Label>Dirección</Label>
              <Input value={form.address} onChange={setField("address")} />
            </div>
            <div>
              <Label>Código postal</Label>
              <Input value={form.postal_code} onChange={setField("postal_code")} />
            </div>
            <div>
              <Label>Ciudad</Label>
              <Input value={form.city} onChange={setField("city")} />
            </div>
            <div>
              <Label>Provincia</Label>
              <Input value={form.province} onChange={setField("province")} />
            </div>
            <div>
              <Label>País</Label>
              <Input value={form.country} onChange={setField("country")} />
            </div>
            <div className="col-span-2">
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notes} onChange={setField("notes")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={submit}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="client-form-submit"
            >
              {editingId ? "Guardar" : "Crear cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
